import {
  DataListDisputeDueProcessFiledBy,
  DataListDisputeDueProcessResolution,
  DataListDisputeStateComplaintResolution,
  DataListDisputeDueProcessRequestForMediationStatus,
  DataListDisputeDueProcessRespondingParty,
  DataListIndicator14OutcomeType,
  DataListIndicator8SampleCount,
  DataListIndicator8SubstitutionType,
  DataListMediationRequester,
  DataListLanguage,
  DataListMonitoringIndicator,
  DataListSppIndicatorGroup,
  DataListSsrSampleCount,
  DataListSsrSubstitutionType,
  DataListStudentType,
  DataListTag,
  DataListTagGroup,
  DataListUserGuide,
  SystemUploadType,
  DataListRelationship,
  DataListMediationScoDpc,
  DataListMediationRequestType,
  DataListMediationOutcome,
  DataListMediationLength,
  DataListMediationHeld,
  DataListMediationFormat,
  DataListMediationCaseType,
  DataListMediationAcommodation,
  DataListMediationA2M,
  DataListParentCallLogIssue,
  DataListIssue,
  DataListFacilitationMeetingType,
  DataListDueProcessHearingType,
  DataListFacilitationMeetingStatus,
  DataListDisputeProcessPersonnel,
  DataListCommunicationPreference,
  DataListAOCStaff,
  DataListAOCComplaintTopic,
  DataListParentCallLogSource,
  DataListParentCallLogSchoolLevel,
  DataListParentCallLogProcess,
  DataListParentCallLogIssueAction,
} from "./../models/data-list";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import {
  DataListContactType,
  DataListSchoolYear,
  DataListTable,
  DataListCapProgramArea,
} from "../models/data-list";
import { DataListRequest } from "../models/common-types";
import { Observable } from "rxjs";
import { shareReplay } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class DataListService {
  private baseUrl = "api/data-list";

  constructor(private http: HttpClient) {}

  getListTables(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListTable[]>(this.baseUrl, { params });
  }

  getContactTypes(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListContactType[]>(
      `${this.baseUrl}/contact-types`,
      { params }
    );
  }

  saveContactType(contactType: DataListContactType) {
    if (!contactType.id) {
      return this.http.post(`${this.baseUrl}/contact-types`, contactType);
    } else {
      return this.http.put(
        `${this.baseUrl}/contact-types/${contactType.code}`,
        contactType
      );
    }
  }

  removeContactType(contactType: DataListContactType) {
    return this.http.delete(
      `${this.baseUrl}/contact-types/${contactType.code}`
    );
  }

  getSchoolYears(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListSchoolYear[]>(`${this.baseUrl}/school-years`, {
      params,
    });
  }

  saveSchoolYear(schoolYear: DataListSchoolYear) {
    if (!schoolYear.id) {
      return this.http.post(`${this.baseUrl}/school-years`, schoolYear);
    } else {
      return this.http.put(
        `${this.baseUrl}/school-years/${schoolYear.code}`,
        schoolYear
      );
    }
  }

  removeSchoolYear(schoolYear: DataListSchoolYear) {
    return this.http.delete(`${this.baseUrl}/school-years/${schoolYear.code}`);
  }

  getTagGroups(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListTagGroup[]>(
      `${this.baseUrl}/tag-groups/paged`,
      {
        params,
      }
    );
  }

  saveTagGroup(tagGroup: DataListTagGroup) {
    this.clearTagGroupCache();
    if (!tagGroup.id) {
      return this.http.post(`${this.baseUrl}/tag-groups`, tagGroup);
    } else {
      return this.http.put(
        `${this.baseUrl}/tag-groups/${tagGroup.id}`,
        tagGroup
      );
    }
  }

  removeTagGroup(tagGroup: DataListTagGroup) {
    this.clearTagGroupCache();
    return this.http.delete(`${this.baseUrl}/tag-groups/${tagGroup.id}`);
  }

  tagGroupsData$: Observable<DataListTagGroup[]>;
  tagGroups: DataListTagGroup[];

  private clearTagGroupCache() {
    this.tagGroupsData$ = null;
    this.tagGroups = [];
  }

  getAllowedTagGroups() {
    if (!this.tagGroupsData$) {
      this.tagGroupsData$ = this.http
        .get<DataListTagGroup[]>(`${this.baseUrl}/tag-groups/byrole`)
        .pipe(shareReplay(1));
      this.tagGroupsData$.subscribe((data) => (this.tagGroups = data));
    }
    return this.tagGroupsData$;
  }

  getTags(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListTag[]>(`${this.baseUrl}/tags`, {
      params,
    });
  }

  saveTag(tag: DataListTag) {
    this.clearTagCache();
    this.clearTagGroupCache();
    if (!tag.id) {
      return this.http.post(`${this.baseUrl}/tags`, tag);
    } else {
      return this.http.put(`${this.baseUrl}/tags/${tag.id}`, tag);
    }
  }

  removeTag(tag: DataListTag) {
    this.clearTagCache();
    this.clearTagGroupCache();
    return this.http.delete(`${this.baseUrl}/tags/${tag.id}`);
  }

  tagData$: Observable<DataListTag[]>;
  tags: DataListTag[];

  private clearTagCache() {
    this.tagData$ = null;
    this.tags = [];
  }

  getAllTags() {
    if (!this.tagData$) {
      this.tagData$ = this.http
        .get<DataListTag[]>(`${this.baseUrl}/tags/all`)
        .pipe(shareReplay(1));
      this.tagData$.subscribe((data) => (this.tags = data));
    }
    return this.tagData$;
  }

  private getParams(request: DataListRequest) {
    let params = new HttpParams();

    Object.keys(request).forEach((k) => {
      if (
        (typeof request[k] !== "object" || request[k] instanceof Date) &&
        request[k] !== null
      ) {
        params = params.set(k, String(request[k]));
      }
    });

    return params;
  }

  getUserGuides(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListUserGuide[]>(`${this.baseUrl}/user-guides`, {
      params,
    });
  }

  saveUserGuide(userGuide: DataListUserGuide) {
    if (!userGuide.id) {
      return this.http.post(`${this.baseUrl}/user-guides`, userGuide);
    } else {
      return this.http.put(`${this.baseUrl}/user-guides`, userGuide);
    }
  }

  removeUserGuide(userGuide: DataListUserGuide) {
    return this.http.delete(`${this.baseUrl}/user-guides/${userGuide.id}`);
  }

  getCapProgramAreas() {
    return this.http.get<DataListCapProgramArea[]>(
      `${this.baseUrl}/cap-programareas`
    );
  }

  saveCapProgramArea(capProgramArea: DataListCapProgramArea) {
    if (!capProgramArea.id) {
      return this.http.post(`${this.baseUrl}/cap-programareas`, capProgramArea);
    } else {
      return this.http.put(
        `${this.baseUrl}/cap-programareas/${capProgramArea.id}`,
        capProgramArea
      );
    }
  }

  removeCapProgramArea(capProgramArea: DataListCapProgramArea) {
    return this.http.delete(
      `${this.baseUrl}/cap-programareas/${capProgramArea.id}`
    );
  }

  getSystemUploadTypes(request: DataListRequest): Observable<SystemUploadType[]> {
    const params = this.getParams(request);

    return this.http.get<SystemUploadType[]>(
      `${this.baseUrl}/system-upload-types`,
      { params }
    );
  }

  saveSystemUploadType(
    systemUploadType: SystemUploadType
  ): Observable<unknown> {
    if (!systemUploadType.id) {
      return this.http.post(
        `${this.baseUrl}/system-upload-types`,
        systemUploadType
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/system-upload-types/${systemUploadType.id}`,
        systemUploadType
      );
    }
  }

  removeSystemUploadType(
    systemUploadType: SystemUploadType
  ): Observable<unknown> {
    return this.http.delete(
      `${this.baseUrl}/system-upload-types/${systemUploadType.id}`
    );
  }

  getDisputeDueProcessFiledBies(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListDisputeDueProcessFiledBy[]>(
      `${this.baseUrl}/due-process-filed-by`,
      { params }
    );
  }

  saveDisputeDueProcessFiledBy(
    disputeDueProcessFiledBy: DataListDisputeDueProcessFiledBy
  ) {
    if (!disputeDueProcessFiledBy.id) {
      return this.http.post(
        `${this.baseUrl}/due-process-filed-by`,
        disputeDueProcessFiledBy
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/due-process-filed-by/${disputeDueProcessFiledBy.id}`,
        disputeDueProcessFiledBy
      );
    }
  }

  removeDisputeDueProcessFiledBy(
    disputeDueProcessFiledBy: DataListDisputeDueProcessFiledBy
  ) {
    return this.http.delete(
      `${this.baseUrl}/due-process-filed-by/${disputeDueProcessFiledBy.id}`
    );
  }

  getDisputeDueProcessResolutions(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListDisputeDueProcessResolution[]>(
      `${this.baseUrl}/due-process-resolutions`,
      { params }
    );
  }

  saveDisputeDueProcessResolution(
    disputeDueProcessResolution: DataListDisputeDueProcessResolution
  ) {
    if (!disputeDueProcessResolution.id) {
      return this.http.post(
        `${this.baseUrl}/due-process-resolutions`,
        disputeDueProcessResolution
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/due-process-resolutions/${disputeDueProcessResolution.id}`,
        disputeDueProcessResolution
      );
    }
  }

  removeDisputeDueProcessResolution(
    disputeDueProcessResolution: DataListDisputeDueProcessResolution
  ) {
    return this.http.delete(
      `${this.baseUrl}/due-process-resolutions/${disputeDueProcessResolution.id}`
    );
  }

  getDisputeStateComplaintResolutions(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListDisputeStateComplaintResolution[]>(
      `${this.baseUrl}/state-complaint-resolutions`,
      { params }
    );
  }

  saveDisputeStateComplaintResolution(
    disputeStateComplaintResolution: DataListDisputeStateComplaintResolution
  ) {
    if (!disputeStateComplaintResolution.id) {
      return this.http.post(
        `${this.baseUrl}/state-complaint-resolutions`,
        disputeStateComplaintResolution
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/state-complaint-resolutions/${disputeStateComplaintResolution.id}`,
        disputeStateComplaintResolution
      );
    }
  }

  removeDisputeStateComplaintResolution(
    disputeStateComplaintResolution: DataListDisputeStateComplaintResolution
  ) {
    return this.http.delete(
      `${this.baseUrl}/state-complaint-resolutions/${disputeStateComplaintResolution.id}`
    );
  }

  getDisputeDueProcessRequestForMediationStatuses(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListDisputeDueProcessRequestForMediationStatus[]>(
      `${this.baseUrl}/due-process-request-for-mediation-status`,
      { params }
    );
  }

  saveDisputeDueProcessRequestForMediationStatus(
    model: DataListDisputeDueProcessRequestForMediationStatus
  ) {
    if (!model.id) {
      return this.http.post(
        `${this.baseUrl}/due-process-request-for-mediation-status`,
        model
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/due-process-request-for-mediation-status/${model.id}`,
        model
      );
    }
  }

  removeDisputeDueProcessRequestForMediationStatus(
    model: DataListDisputeDueProcessRequestForMediationStatus
  ) {
    return this.http.delete(
      `${this.baseUrl}/due-process-request-for-mediation-status/${model.id}`
    );
  }

  getDisputeDueProcessRespondingParty(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListDisputeDueProcessRespondingParty[]>(
      `${this.baseUrl}/due-process-responding-party`,
      { params }
    );
  }

  saveDisputeDueProcessRespondingParty(
    disputeDueProcessRespondingParty: DataListDisputeDueProcessRespondingParty
  ) {
    if (!disputeDueProcessRespondingParty.id) {
      return this.http.post(
        `${this.baseUrl}/due-process-responding-party`,
        disputeDueProcessRespondingParty
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/due-process-responding-party/${disputeDueProcessRespondingParty.id}`,
        disputeDueProcessRespondingParty
      );
    }
  }

  removeDisputeDueProcessRespondingParty(
    disputeDueProcessRespondingParty: DataListDisputeDueProcessRespondingParty
  ) {
    return this.http.delete(
      `${this.baseUrl}/due-process-responding-party/${disputeDueProcessRespondingParty.id}`
    );
  }

  getMediationRequesters(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationRequester[]>(
      `${this.baseUrl}/mediation-requester`,
      { params }
    );
  }

  saveMediationRequester(
    disputeMediationRequester: DataListMediationRequester
  ) {
    if (!disputeMediationRequester.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-requester`,
        disputeMediationRequester
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-requester/${disputeMediationRequester.id}`,
        disputeMediationRequester
      );
    }
  }

  removeMediationRequester(
    disputeMediationRequester: DataListMediationRequester
  ) {
    return this.http.delete(
      `${this.baseUrl}/mediation-requester/${disputeMediationRequester.id}`
    );
  }

  getMediationScoDpcs(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationScoDpc[]>(
      `${this.baseUrl}/mediation-sco-dpc`,
      { params }
    );
  }

  saveMediationScoDpc(disputeMediationScoDpc: DataListMediationScoDpc) {
    if (!disputeMediationScoDpc.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-sco-dpc`,
        disputeMediationScoDpc
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-sco-dpc/${disputeMediationScoDpc.id}`,
        disputeMediationScoDpc
      );
    }
  }

  removeMediationScoDpc(disputeMediationScoDpc: DataListMediationScoDpc) {
    return this.http.delete(
      `${this.baseUrl}/mediation-sco-dpc/${disputeMediationScoDpc.id}`
    );
  }

  getMediationRequestTypes(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationRequestType[]>(
      `${this.baseUrl}/mediation-request-types`,
      { params }
    );
  }

  saveMediationRequestType(
    disputeMediationRequestType: DataListMediationRequestType
  ) {
    if (!disputeMediationRequestType.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-request-types`,
        disputeMediationRequestType
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-request-types/${disputeMediationRequestType.id}`,
        disputeMediationRequestType
      );
    }
  }

  removeMediationRequestType(
    disputeMediationRequestType: DataListMediationRequestType
  ) {
    return this.http.delete(
      `${this.baseUrl}/mediation-request-types/${disputeMediationRequestType.id}`
    );
  }

  getMediationOutcomes(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationOutcome[]>(
      `${this.baseUrl}/mediation-outcomes`,
      { params }
    );
  }

  saveMediationOutcome(disputeMediationOutcome: DataListMediationOutcome) {
    if (!disputeMediationOutcome.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-outcomes`,
        disputeMediationOutcome
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-outcomes/${disputeMediationOutcome.id}`,
        disputeMediationOutcome
      );
    }
  }

  removeMediationOutcome(disputeMediationOutcomes: DataListMediationOutcome) {
    return this.http.delete(
      `${this.baseUrl}/mediation-outcomes/${disputeMediationOutcomes.id}`
    );
  }

  getMediationLength(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationLength[]>(
      `${this.baseUrl}/mediation-length`,
      { params }
    );
  }

  saveMediationLength(disputeMediationLength: DataListMediationLength) {
    if (!disputeMediationLength.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-length`,
        disputeMediationLength
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-length/${disputeMediationLength.id}`,
        disputeMediationLength
      );
    }
  }

  removeMediationLength(disputeMediationLength: DataListMediationLength) {
    return this.http.delete(
      `${this.baseUrl}/mediation-length/${disputeMediationLength.id}`
    );
  }

  getMediationHeld(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationHeld[]>(
      `${this.baseUrl}/mediation-held`,
      { params }
    );
  }

  saveMediationHeld(disputeMediationHeld: DataListMediationHeld) {
    if (!disputeMediationHeld.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-held`,
        disputeMediationHeld
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-held/${disputeMediationHeld.id}`,
        disputeMediationHeld
      );
    }
  }

  removeMediationHeld(disputeMediationHeld: DataListMediationHeld) {
    return this.http.delete(
      `${this.baseUrl}/mediation-held/${disputeMediationHeld.id}`
    );
  }

  getMediationFormat(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationFormat[]>(
      `${this.baseUrl}/mediation-format`,
      { params }
    );
  }

  saveMediationFormat(disputeMediationFormat: DataListMediationFormat) {
    if (!disputeMediationFormat.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-format`,
        disputeMediationFormat
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-format/${disputeMediationFormat.id}`,
        disputeMediationFormat
      );
    }
  }

  removeMediationFormat(disputeMediationFormat: DataListMediationFormat) {
    return this.http.delete(
      `${this.baseUrl}/mediation-format/${disputeMediationFormat.id}`
    );
  }

  getMediationAccommodations(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationAcommodation[]>(
      `${this.baseUrl}/mediation-accommodations`,
      { params }
    );
  }

  saveMediationAccommodation(
    disputeMediationAccommodation: DataListMediationAcommodation
  ) {
    if (!disputeMediationAccommodation.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-accommodations`,
        disputeMediationAccommodation
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-accommodations/${disputeMediationAccommodation.id}`,
        disputeMediationAccommodation
      );
    }
  }

  removeMediationAccommodation(
    disputeMediationAccommodation: DataListMediationAcommodation
  ) {
    return this.http.delete(
      `${this.baseUrl}/mediation-accommodations/${disputeMediationAccommodation.id}`
    );
  }

  getMediationCaseTypes(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationCaseType[]>(
      `${this.baseUrl}/mediation-case-types`,
      { params }
    );
  }

  saveMediationCaseType(disputeMediationCaseType: DataListMediationCaseType) {
    if (!disputeMediationCaseType.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-case-types`,
        disputeMediationCaseType
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-case-types/${disputeMediationCaseType.id}`,
        disputeMediationCaseType
      );
    }
  }

  removeMediationCaseType(disputeMediationCaseType: DataListMediationCaseType) {
    return this.http.delete(
      `${this.baseUrl}/mediation-case-types/${disputeMediationCaseType.id}`
    );
  }

  getMediationA2Ms(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListMediationA2M[]>(
      `${this.baseUrl}/mediation-a2m`,
      { params }
    );
  }

  saveMediationA2M(disputeMediationA2M: DataListMediationA2M) {
    if (!disputeMediationA2M.id) {
      return this.http.post(
        `${this.baseUrl}/mediation-a2m`,
        disputeMediationA2M
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/mediation-a2m/${disputeMediationA2M.id}`,
        disputeMediationA2M
      );
    }
  }

  removeMediationA2M(disputeMediationA2M: DataListMediationA2M) {
    return this.http.delete(
      `${this.baseUrl}/mediation-a2m/${disputeMediationA2M.id}`
    );
  }

  getLanguages(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListLanguage[]>(`${this.baseUrl}/languages`, {
      params,
    });
  }

  saveLanguage(disputeLanguage: DataListLanguage) {
    if (!disputeLanguage.id) {
      return this.http.post(`${this.baseUrl}/languages`, disputeLanguage);
    } else {
      return this.http.put(
        `${this.baseUrl}/languages/${disputeLanguage.id}`,
        disputeLanguage
      );
    }
  }

  removeLanguage(disputeLanguage: DataListLanguage) {
    return this.http.delete(`${this.baseUrl}/languages/${disputeLanguage.id}`);
  }

  getRelationships(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListRelationship[]>(
      `${this.baseUrl}/relationships`,
      { params }
    );
  }

  saveRelationship(relationship: DataListRelationship) {
    if (!relationship.id) {
      return this.http.post(`${this.baseUrl}/relationships`, relationship);
    } else {
      return this.http.put(
        `${this.baseUrl}/relationships/${relationship.id}`,
        relationship
      );
    }
  }

  removeRelationship(relationship: DataListRelationship) {
    return this.http.delete(`${this.baseUrl}/relationships/${relationship.id}`);
  }

  getIssues(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListIssue[]>(`${this.baseUrl}/issues`, { params });
  }

  saveIssue(issue: DataListIssue) {
    if (!issue.id) {
      return this.http.post(`${this.baseUrl}/issues`, issue);
    } else {
      return this.http.put(`${this.baseUrl}/issues/${issue.id}`, issue);
    }
  }

  removeIssue(issue: DataListIssue) {
    return this.http.delete(`${this.baseUrl}/issues/${issue.id}`);
  }

  getParentCallLogIssues(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListParentCallLogIssue[]>(
      `${this.baseUrl}/parent-call-log-issues`,
      { params }
    );
  }

  saveParentCallLogIssue(parentCallLogIssue: DataListParentCallLogIssue) {
    if (!parentCallLogIssue.id) {
      return this.http.post(
        `${this.baseUrl}/parent-call-log-issues`,
        parentCallLogIssue
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/parent-call-log-issues/${parentCallLogIssue.id}`,
        parentCallLogIssue
      );
    }
  }

  removeParentCallLogIssue(parentCallLogIssue: DataListParentCallLogIssue) {
    return this.http.delete(
      `${this.baseUrl}/parent-call-log-issues/${parentCallLogIssue.id}`
    );
  }

  getFacilitationMeetingTypes(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListFacilitationMeetingType[]>(
      `${this.baseUrl}/facilitation-meeting-types`,
      { params }
    );
  }

  saveFacilitationMeetingType(
    facilitationMeetingType: DataListFacilitationMeetingType
  ) {
    if (!facilitationMeetingType.id) {
      return this.http.post(
        `${this.baseUrl}/facilitation-meeting-types`,
        facilitationMeetingType
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/facilitation-meeting-types/${facilitationMeetingType.id}`,
        facilitationMeetingType
      );
    }
  }

  removeFacilitationMeetingType(
    facilitationMeetingType: DataListFacilitationMeetingType
  ) {
    return this.http.delete(
      `${this.baseUrl}/facilitation-meeting-types/${facilitationMeetingType.id}`
    );
  }

  getDueProcessHearingTypes(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListDueProcessHearingType[]>(
      `${this.baseUrl}/due-process-hearing-types`,
      { params }
    );
  }

  saveDueProcessHearingType(
    dueProcessHearingType: DataListDueProcessHearingType
  ) {
    if (!dueProcessHearingType.id) {
      return this.http.post(
        `${this.baseUrl}/due-process-hearing-types`,
        dueProcessHearingType
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/due-process-hearing-types/${dueProcessHearingType.id}`,
        dueProcessHearingType
      );
    }
  }

  removeDueProcessHearingType(
    dueProcessHearingType: DataListDueProcessHearingType
  ) {
    return this.http.delete(
      `${this.baseUrl}/due-process-hearing-types/${dueProcessHearingType.id}`
    );
  }

  getFacilitationMeetingStatuses(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListFacilitationMeetingStatus[]>(
      `${this.baseUrl}/facilitation-meeting-status`,
      { params }
    );
  }

  saveFacilitationMeetingStatus(
    facilitationMeetingStatus: DataListFacilitationMeetingStatus
  ) {
    if (!facilitationMeetingStatus.id) {
      return this.http.post(
        `${this.baseUrl}/facilitation-meeting-status`,
        facilitationMeetingStatus
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/facilitation-meeting-status/${facilitationMeetingStatus.id}`,
        facilitationMeetingStatus
      );
    }
  }

  removeFacilitationMeetingStatus(
    facilitationMeetingStatus: DataListFacilitationMeetingStatus
  ) {
    return this.http.delete(
      `${this.baseUrl}/facilitation-meeting-status/${facilitationMeetingStatus.id}`
    );
  }

  getDisputeProcessPersonnel(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListDisputeProcessPersonnel[]>(
      `${this.baseUrl}/dispute-personnel`,
      { params }
    );
  }

  saveDisputeProcessPersonnel(
    disputeProcessPersonnel: DataListDisputeProcessPersonnel
  ) {
    if (!disputeProcessPersonnel.id) {
      return this.http.post(
        `${this.baseUrl}/dispute-personnel`,
        disputeProcessPersonnel
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/dispute-personnel/${disputeProcessPersonnel.id}`,
        disputeProcessPersonnel
      );
    }
  }

  removeDisputeProcessPersonnel(
    disputeProcessPersonnel: DataListDisputeProcessPersonnel
  ) {
    return this.http.delete(
      `${this.baseUrl}/dispute-personnel/${disputeProcessPersonnel.id}`
    );
  }

  getCommunicationPreferences(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListCommunicationPreference[]>(
      `${this.baseUrl}/communication-preference`,
      { params }
    );
  }

  saveCommunicationPreference(
    communicationPreference: DataListCommunicationPreference
  ) {
    if (!communicationPreference.id) {
      return this.http.post(
        `${this.baseUrl}/communication-preference`,
        communicationPreference
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/communication-preference/${communicationPreference.id}`,
        communicationPreference
      );
    }
  }

  removeCommunicationPreference(
    communicationPreference: DataListCommunicationPreference
  ) {
    return this.http.delete(
      `${this.baseUrl}/communication-preference/${communicationPreference.id}`
    );
  }

  getAOCStaff(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListAOCStaff[]>(`${this.baseUrl}/aoc-staff`, {
      params,
    });
  }

  saveAOCStaff(aocStaff: DataListAOCStaff) {
    if (!aocStaff.id) {
      return this.http.post(`${this.baseUrl}/aoc-staff`, aocStaff);
    } else {
      return this.http.put(
        `${this.baseUrl}/aoc-staff/${aocStaff.id}`,
        aocStaff
      );
    }
  }

  removeAOCStaff(aocStaff: DataListAOCStaff) {
    return this.http.delete(`${this.baseUrl}/aoc-staff/${aocStaff.id}`);
  }

  getAOCComplaintTopics(request: DataListRequest) {
    let params = this.getParams(request);

    return this.http.get<DataListAOCComplaintTopic[]>(
      `${this.baseUrl}/aoc-complaint-topic`,
      { params }
    );
  }

  saveAOCComplaintTopic(aocComplaintTopic: DataListAOCComplaintTopic) {
    if (!aocComplaintTopic.id) {
      return this.http.post(
        `${this.baseUrl}/aoc-complaint-topic`,
        aocComplaintTopic
      );
    } else {
      return this.http.put(
        `${this.baseUrl}/aoc-complaint-topic/${aocComplaintTopic.id}`,
        aocComplaintTopic
      );
    }
  }

  removeAOCComplaintTopic(aocComplaintTopic: DataListAOCComplaintTopic) {
    return this.http.delete(
      `${this.baseUrl}/aoc-complaint-topic/${aocComplaintTopic.id}`
    );
  }

  getParentCallLogSources(request: DataListRequest) {
    let params = this.getParams(request);
    return this.http.get<DataListParentCallLogSource[]>(
      `${this.baseUrl}/parent-call-log-sources`,
      { params }
    );
  }

  saveParentCallLogSource(model: DataListParentCallLogSource) {
    if (!model.id) {
      return this.http.post(`${this.baseUrl}/parent-call-log-sources`, model);
    } else {
      return this.http.put(
        `${this.baseUrl}/parent-call-log-sources/${model.id}`,
        model
      );
    }
  }

  removeParentCallLogSource(model: DataListParentCallLogSource) {
    return this.http.delete(
      `${this.baseUrl}/parent-call-log-sources/${model.id}`
    );
  }

  getParentCallLogSchoolLevels(request: DataListRequest) {
    let params = this.getParams(request);
    return this.http.get<DataListParentCallLogSchoolLevel[]>(
      `${this.baseUrl}/parent-call-log-school-levels`,
      { params }
    );
  }

  saveParentCallLogSchoolLevel(model: DataListParentCallLogSchoolLevel) {
    if (!model.id) {
      return this.http.post(`${this.baseUrl}/parent-call-log-school-levels`, model);
    } else {
      return this.http.put(
        `${this.baseUrl}/parent-call-log-school-levels/${model.id}`,
        model
      );
    }
  }

  removeParentCallLogSchoolLevel(model: DataListParentCallLogSchoolLevel) {
    return this.http.delete(
      `${this.baseUrl}/parent-call-log-school-levels/${model.id}`
    );
  }

  getParentCallLogProcesses(request: DataListRequest) {
    let params = this.getParams(request);
    return this.http.get<DataListParentCallLogProcess[]>(
      `${this.baseUrl}/parent-call-log-processes`,
      { params }
    );
  }

  saveParentCallLogProcess(model: DataListParentCallLogProcess) {
    if (!model.id) {
      return this.http.post(`${this.baseUrl}/parent-call-log-processes`, model);
    } else {
      return this.http.put(
        `${this.baseUrl}/parent-call-log-processes/${model.id}`,
        model
      );
    }
  }

  removeParentCallLogProcess(model: DataListParentCallLogProcess) {
    return this.http.delete(
      `${this.baseUrl}/parent-call-log-processes/${model.id}`
    );
  }

  getParentCallLogIssueActions(request: DataListRequest) {
    let params = this.getParams(request);
    return this.http.get<DataListParentCallLogIssueAction[]>(
      `${this.baseUrl}/parent-call-log-issue-actions`,
      { params }
    );
  }

  saveParentCallLogIssueAction(model: DataListParentCallLogIssueAction) {
    if (!model.id) {
      return this.http.post(`${this.baseUrl}/parent-call-log-issue-actions`, model);
    } else {
      return this.http.put(
        `${this.baseUrl}/parent-call-log-issue-actions/${model.id}`,
        model
      );
    }
  }

  removeParentCallLogIssueAction(model: DataListParentCallLogIssueAction) {
    return this.http.delete(
      `${this.baseUrl}/parent-call-log-issue-actions/${model.id}`
    );
  }
}
